import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Button,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  LinearProgress,
  Stack,
  Divider,
  alpha,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  SmartToy as SmartToyIcon,
  Propane as PropaneIcon,
  PendingActions as PendingActionsIcon,
  CheckCircle as CheckCircleIcon,
  AutoMode as AutoModeIcon,
  Analytics as AnalyticsIcon,
  OpenInNew as OpenInNewIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  RocketLaunch as RocketLaunchIcon,
} from '@mui/icons-material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { dashboardApi, invoicesApi, aiMailApi, plantsApi } from '../services/api';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { formatNuf } from '../utils/formatNuf';
import { formatCustomerName } from '../utils/formatCustomerName';
import AssistantChecklistSection from '../components/assistant/AssistantChecklistSection';

// Componente Card Statistica (compatta per 6 colonne)
const StatCard = ({ title, value, icon, color, subtitle, loading, onClick }) => (
  <Card
    sx={{
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 0.2s',
      '&:hover': onClick ? { boxShadow: 4 } : {},
    }}
    onClick={onClick}
  >
    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" noWrap>
            {title}
          </Typography>
          {loading ? (
            <Skeleton width={60} height={36} />
          ) : (
            <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
              {value}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1,
            borderRadius: 1.5,
            bgcolor: `${color}.light`,
            color: `${color}.main`,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Barra livello impianto colorata
const LevelBar = ({ percent, threshold }) => {
  const color = percent <= threshold ? '#f44336' : percent <= 30 ? '#ff9800' : '#4caf50';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <LinearProgress
        variant="determinate"
        value={Math.min(percent, 100)}
        sx={{
          flex: 1,
          height: 8,
          borderRadius: 4,
          bgcolor: alpha(color, 0.15),
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 },
        }}
      />
      <Typography variant="caption" fontWeight="bold" sx={{ color, minWidth: 36, textAlign: 'right' }}>
        {percent.toFixed(0)}%
      </Typography>
    </Box>
  );
};

// Formatta importo in Euro
const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(value || 0);
};

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Genera lista anni (ultimi 5 anni)
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Mutation redeploy Railway
  const redeployMutation = useMutation({
    mutationFn: () => dashboardApi.redeploy(),
    onSuccess: () => setSnackbar({ open: true, message: 'Redeploy avviato! L\'app si aggiornerà a breve.', severity: 'success' }),
    onError: (err) => setSnackbar({ open: true, message: err?.response?.data?.message || 'Errore nel redeploy', severity: 'error' }),
  });

  // Query statistiche - React Query v5 syntax
  const { 
    data: stats, 
    isLoading: statsLoading, 
    error: statsError,
    refetch: refetchStats 
  } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: () => dashboardApi.getStats().then(res => res.data?.data)
  });

  // Query fatture scadute
  const { 
    data: overdueInvoices, 
    isLoading: overdueLoading 
  } = useQuery({
    queryKey: ['overdueInvoices'],
    queryFn: () => invoicesApi.getOverdue().then(res => res.data?.data || [])
  });

  // Query cash flow (ricavi vs costi) mensili
  const { 
    data: cashFlowData, 
    isLoading: cashFlowLoading,
    refetch: refetchCashFlow
  } = useQuery({
    queryKey: ['cashFlow', selectedYear],
    queryFn: () => dashboardApi.getCashFlow({ year: selectedYear }).then(res => res.data?.data || [])
  });

  // Query AI dashboard stats
  const {
    data: aiStats,
    isLoading: aiStatsLoading,
  } = useQuery({
    queryKey: ['ai-dashboard-stats'],
    queryFn: () => aiMailApi.getDashboardStats().then(res => res.data?.stats || {}),
    refetchInterval: 60000,
    retry: 1,
    staleTime: 30000,
  });

  // Query ultime email pending (per pannello AI)
  const {
    data: aiPendingActions,
    isLoading: aiPendingLoading,
  } = useQuery({
    queryKey: ['ai-pending-dashboard'],
    queryFn: () => aiMailApi.getPendingActions({ limit: 10, status: 'pending' }).then(res => res.data?.actions || []),
    refetchInterval: 60000,
    retry: 1,
    staleTime: 30000,
  });

  // Query riepilogo impianti
  const {
    data: plantsSummary,
    isLoading: plantsSummaryLoading,
  } = useQuery({
    queryKey: ['plants-summary'],
    queryFn: () => dashboardApi.getPlantsSummary().then(res => res.data?.data || {}),
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Query 5 impianti con livello stimato più basso
  const {
    data: lowestPlants,
    isLoading: lowestPlantsLoading,
  } = useQuery({
    queryKey: ['plants-lowest-level'],
    queryFn: () => plantsApi.getAll({
      page: 1, limit: 5,
      sortField: 'current_level_percent', sortOrder: 'ASC',
      attivo: 'true',
    }).then(res => res.data?.data?.rows || []),
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Prepara i dati del cash flow con margine splittato per colore verde/rosso
  const cashFlowChartData = React.useMemo(() => {
    if (!cashFlowData) return [];
    return cashFlowData.map((item, idx, arr) => ({
      ...item,
      marginePos: item.margine >= 0 ? item.margine : null,
      margineNeg: item.margine < 0 ? item.margine : null,
      ...(idx > 0 && arr[idx - 1].margine >= 0 !== item.margine >= 0 ? {
        marginePos: item.margine >= 0 ? item.margine : (item.margine < 0 ? item.margine : null),
        margineNeg: item.margine < 0 ? item.margine : (item.margine >= 0 ? item.margine : null),
      } : {})
    }));
  }, [cashFlowData]);

  // Custom dot per la linea margine: verde se >= 0, rosso se < 0
  const MargineDot = (props) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    const color = (payload?.margine ?? 0) >= 0 ? '#4caf50' : '#f44336';
    return <circle cx={cx} cy={cy} r={5} fill={color} stroke="#fff" strokeWidth={2} />;
  };

  if (statsError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Errore nel caricamento dei dati. Verifica la connessione al server.
      </Alert>
    );
  }

  const aiPending = aiStats?.pending || 0;
  const plantsTotal = plantsSummary?.total || 0;
  const plantsCritical = plantsSummary?.critical || 0;

  return (
    <Box sx={{ p: { xs: 0, sm: 0 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isMobile ? 1 : 3 }}>
        <Box>
          <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold">
            Dashboard
          </Typography>
          {!isMobile && <Typography variant="body2" color="text.secondary">
            Panoramica del sistema di gestione
          </Typography>}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Aggiorna all'ultima versione (Railway redeploy)">
            <IconButton
              onClick={() => redeployMutation.mutate()}
              disabled={redeployMutation.isPending}
              size="small"
              sx={{
                bgcolor: redeployMutation.isPending ? 'action.disabledBackground' : 'primary.light',
                color: 'primary.main',
                '&:hover': { bgcolor: 'primary.main', color: '#fff' },
              }}
            >
              {redeployMutation.isPending ? <CircularProgress size={20} /> : <RocketLaunchIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Aggiorna dati">
            <IconButton onClick={() => { refetchStats(); refetchCashFlow(); }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ═══ ZONA 1 — KPI Strip (6 card compatte) ═══ */}
      <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: isMobile ? 1 : 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Fatture Emesse"
            value={formatCurrency(stats?.issuedAmountLast12Months)}
            icon={<ReceiptIcon />}
            color="primary"
            subtitle="Ultimi 12 mesi"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Fatture Pagate"
            value={formatCurrency(stats?.paidAmountLast12Months)}
            icon={<CheckCircleIcon />}
            color="success"
            subtitle="Ultimi 12 mesi"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Fatture Non Pagate"
            value={formatCurrency(stats?.unpaidAmountLast12Months)}
            icon={<ReceiptIcon />}
            color="warning"
            subtitle="Ultimi 12 mesi"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Fatture Scadute"
            value={formatCurrency(stats?.overdueAmountLast12Months)}
            icon={<WarningIcon />}
            color="error"
            subtitle="Ultimi 12 mesi"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Impianti Critici"
            value={plantsCritical}
            icon={<WarningIcon />}
            color={plantsCritical > 0 ? 'error' : 'success'}
            subtitle={plantsCritical > 0 ? 'Livello basso' : 'Tutto ok'}
            loading={plantsSummaryLoading}
            onClick={() => navigate('/crm/impianti')}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="AI: In Attesa"
            value={aiPending}
            icon={<SmartToyIcon />}
            color={aiPending > 0 ? 'warning' : 'success'}
            subtitle={aiPending > 0 ? 'Richiede review' : 'Nessuna azione'}
            loading={aiStatsLoading}
            onClick={() => navigate('/crm/email/ai-dashboard')}
          />
        </Grid>
      </Grid>

      <AssistantChecklistSection mode="dashboard" />

      {/* ═══ ZONA 2 — Grafico + Pannelli Laterali ═══ */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: isMobile ? 1 : 3 }}>
        {/* Grafico Ricavi vs Costi */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    Ricavi vs Costi
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      sx={{ height: 32 }}
                    >
                      {availableYears.map(year => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <TrendingUpIcon color="success" />
              </Box>
              {cashFlowLoading ? (
                <Skeleton height={isMobile ? 240 : 320} />
              ) : (
                <ResponsiveContainer width="100%" height={isMobile ? 240 : 320}>
                  <ComposedChart data={cashFlowChartData}>
                    <defs>
                      {cashFlowChartData && cashFlowChartData.length > 0 && (() => {
                        const values = cashFlowChartData.map(d => d.margine || 0);
                        const max = Math.max(...values, 0);
                        const min = Math.min(...values, 0);
                        const range = max - min || 1;
                        const zeroPercent = max / range;
                        if (min >= 0) {
                          return (
                            <linearGradient id="margineGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#4caf50" />
                              <stop offset="100%" stopColor="#4caf50" />
                            </linearGradient>
                          );
                        }
                        if (max <= 0) {
                          return (
                            <linearGradient id="margineGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#f44336" />
                              <stop offset="100%" stopColor="#f44336" />
                            </linearGradient>
                          );
                        }
                        return (
                          <linearGradient id="margineGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#4caf50" />
                            <stop offset={`${(zeroPercent * 100).toFixed(1)}%`} stopColor="#4caf50" />
                            <stop offset={`${(zeroPercent * 100).toFixed(1)}%`} stopColor="#f44336" />
                            <stop offset="100%" stopColor="#f44336" />
                          </linearGradient>
                        );
                      })()}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis 
                      yAxisId="left"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
                    />
                    <YAxis 
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
                    />
                    <RechartsTooltip 
                      formatter={(value, name) => {
                        const labels = {
                          ricavi: 'Ricavi (Pag. + Acconti)',
                          costi: 'Costi (Fatt. Fornitori)',
                          daIncassare: 'Da Incassare',
                          margine: 'Margine'
                        };
                        return [formatCurrency(value), labels[name] || name];
                      }}
                      labelStyle={{ fontWeight: 'bold' }}
                      contentStyle={{ borderRadius: 8, border: '1px solid #eee' }}
                    />
                    <Legend 
                      formatter={(value) => {
                        const labels = {
                          ricavi: 'Ricavi (Pag. + Acconti)',
                          costi: 'Costi (Fatt. Fornitori)',
                          daIncassare: 'Da Incassare',
                          margine: 'Margine'
                        };
                        return labels[value] || value;
                      }}
                    />
                    <Bar yAxisId="left" dataKey="ricavi" fill="#4caf50" radius={[4, 4, 0, 0]} name="ricavi" />
                    <Bar yAxisId="left" dataKey="costi" fill="#f44336" radius={[4, 4, 0, 0]} name="costi" />
                    <Bar yAxisId="left" dataKey="daIncassare" fill="#ff9800" radius={[4, 4, 0, 0]} name="daIncassare" />
                    <Line 
                      yAxisId="right" 
                      type="monotone" 
                      dataKey="margine" 
                      stroke="url(#margineGradient)" 
                      strokeWidth={3}
                      dot={<MargineDot />}
                      activeDot={<MargineDot />}
                      name="margine"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
              
              {/* Tabella Resoconto Mensile */}
              {!cashFlowLoading && cashFlowData && cashFlowData.length > 0 && (() => {
                const totals = cashFlowData.reduce((acc, item) => ({
                  ricavi: acc.ricavi + (item.ricavi || 0),
                  costi: acc.costi + (item.costi || 0),
                  daIncassare: acc.daIncassare + (item.daIncassare || 0),
                  margine: acc.margine + (item.margine || 0)
                }), { ricavi: 0, costi: 0, daIncassare: 0, margine: 0 });

                return (
                <Box sx={{ mt: 2, overflowX: 'auto' }}>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: `90px repeat(${cashFlowData.length}, 1fr) 80px`,
                    gap: 0.5,
                    fontSize: '0.85rem',
                    minWidth: 900
                  }}>
                    {/* Header - Mesi */}
                    <Box sx={{ fontWeight: 'bold', p: 0.5, bgcolor: '#f5f5f5', borderRadius: 1 }}></Box>
                    {cashFlowData.map((item) => (
                      <Box key={item.month} sx={{ 
                        fontWeight: 'bold', 
                        p: 0.5, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        textAlign: 'center'
                      }}>
                        {item.month}
                      </Box>
                    ))}
                    <Box sx={{ fontWeight: 'bold', p: 0.5, bgcolor: '#1976d2', color: '#fff', borderRadius: 1, textAlign: 'center' }}>
                      TOTALE
                    </Box>
                    
                    {/* Riga Ricavi */}
                    <Box sx={{ p: 0.5, color: '#4caf50', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                      Ricavi
                    </Box>
                    {cashFlowData.map((item) => (
                      <Box key={`ricavi-${item.month}`} sx={{ 
                        p: 0.5, 
                        textAlign: 'right',
                        color: '#4caf50',
                        bgcolor: 'rgba(76, 175, 80, 0.08)',
                        borderRadius: 1
                      }}>
                        {item.ricavi > 0 ? `€${(item.ricavi / 1000).toFixed(1)}k` : '-'}
                      </Box>
                    ))}
                    <Box sx={{ p: 0.5, textAlign: 'right', color: '#4caf50', bgcolor: 'rgba(76, 175, 80, 0.15)', borderRadius: 1, fontWeight: 'bold' }}>
                      €{(totals.ricavi / 1000).toFixed(1)}k
                    </Box>
                    
                    {/* Riga Costi */}
                    <Box sx={{ p: 0.5, color: '#f44336', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                      Costi
                    </Box>
                    {cashFlowData.map((item) => (
                      <Box key={`costi-${item.month}`} sx={{ 
                        p: 0.5, 
                        textAlign: 'right',
                        color: '#f44336',
                        bgcolor: 'rgba(244, 67, 54, 0.08)',
                        borderRadius: 1
                      }}>
                        {item.costi > 0 ? `€${(item.costi / 1000).toFixed(1)}k` : '-'}
                      </Box>
                    ))}
                    <Box sx={{ p: 0.5, textAlign: 'right', color: '#f44336', bgcolor: 'rgba(244, 67, 54, 0.15)', borderRadius: 1, fontWeight: 'bold' }}>
                      €{(totals.costi / 1000).toFixed(1)}k
                    </Box>
                    
                    {/* Riga Da Incassare */}
                    <Box sx={{ p: 0.5, color: '#ff9800', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                      Da Incass.
                    </Box>
                    {cashFlowData.map((item) => (
                      <Box key={`daIncassare-${item.month}`} sx={{ 
                        p: 0.5, 
                        textAlign: 'right',
                        color: '#ff9800',
                        bgcolor: 'rgba(255, 152, 0, 0.08)',
                        borderRadius: 1
                      }}>
                        {item.daIncassare > 0 ? `€${(item.daIncassare / 1000).toFixed(1)}k` : '-'}
                      </Box>
                    ))}
                    <Box sx={{ p: 0.5, textAlign: 'right', color: '#ff9800', bgcolor: 'rgba(255, 152, 0, 0.15)', borderRadius: 1, fontWeight: 'bold' }}>
                      €{(totals.daIncassare / 1000).toFixed(1)}k
                    </Box>
                    
                    {/* Riga Margine */}
                    <Box sx={{ p: 0.5, color: totals.margine >= 0 ? '#4caf50' : '#f44336', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                      Margine
                    </Box>
                    {cashFlowData.map((item) => (
                      <Box key={`margine-${item.month}`} sx={{ 
                        p: 0.5, 
                        textAlign: 'right',
                        color: item.margine >= 0 ? '#4caf50' : '#f44336',
                        bgcolor: item.margine >= 0 ? 'rgba(76, 175, 80, 0.08)' : 'rgba(244, 67, 54, 0.08)',
                        borderRadius: 1,
                        fontWeight: 'bold'
                      }}>
                        {item.margine !== 0 ? `€${(item.margine / 1000).toFixed(1)}k` : '-'}
                      </Box>
                    ))}
                    <Box sx={{ 
                      p: 0.5, 
                      textAlign: 'right', 
                      color: totals.margine >= 0 ? '#4caf50' : '#f44336', 
                      bgcolor: totals.margine >= 0 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)', 
                      borderRadius: 1, 
                      fontWeight: 'bold' 
                    }}>
                      €{(totals.margine / 1000).toFixed(1)}k
                    </Box>
                  </Box>
                </Box>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>

        {/* Colonna Destra — AI Assistant + Stato Impianti */}
        <Grid item xs={12} md={4}>
          <Stack spacing={isMobile ? 1.5 : 3}>
            {/* ── Pannello AI Assistente Email ── */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmartToyIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      AI Assistente
                    </Typography>
                  </Box>
                  <Tooltip title="Apri AI Dashboard">
                    <IconButton size="small" onClick={() => navigate('/crm/email/ai-dashboard')}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {aiStatsLoading ? (
                  <>
                    <Skeleton height={40} />
                    <Skeleton height={40} />
                    <Skeleton height={40} />
                  </>
                ) : !aiStats || (aiStats.analyzed_total === 0 && aiStats.pending === 0) ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <SmartToyIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      AI non ancora configurato o nessuna email analizzata
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1 }}
                      onClick={() => navigate('/crm/impostazioni')}
                    >
                      Configura
                    </Button>
                  </Box>
                ) : (
                  <>
                    {/* Alert azioni pending */}
                    {aiPending > 0 && (
                      <Alert
                        severity="warning"
                        sx={{ mb: 2, cursor: 'pointer' }}
                        onClick={() => navigate('/crm/email/ai-dashboard')}
                        action={
                          <Chip label={aiPending} size="small" color="warning" />
                        }
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {aiPending} {aiPending === 1 ? 'azione richiede' : 'azioni richiedono'} review
                        </Typography>
                      </Alert>
                    )}

                    {/* Stats mini-grid */}
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                          <Typography variant="h6" fontWeight="bold" color="success.main">
                            {aiStats.approved_today || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Approvate oggi
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                          <AutoModeIcon sx={{ color: 'info.main', fontSize: 20 }} />
                          <Typography variant="h6" fontWeight="bold" color="info.main">
                            {aiStats.auto_applied_today || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Auto-applicate
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AnalyticsIcon fontSize="small" color="primary" />
                            <Typography variant="body2">Email analizzate totali</Typography>
                          </Box>
                          <Typography variant="body2" fontWeight="bold">
                            {aiStats.analyzed_total || 0}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Breakdown per categoria */}
                    {aiStats.by_category && Object.keys(aiStats.by_category).length > 0 && (
                      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Object.entries(aiStats.by_category).map(([cat, count]) => (
                          <Chip
                            key={cat}
                            label={`${cat}: ${count}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ))}
                      </Box>
                    )}

                    {/* Lista ultime email che richiedono azione */}
                    {aiPendingLoading ? (
                      <Box sx={{ mt: 2 }}>
                        {[...Array(3)].map((_, i) => <Skeleton key={i} height={36} sx={{ mb: 0.5 }} />)}
                      </Box>
                    ) : (aiPendingActions || []).length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 1 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                          Email in attesa di review
                        </Typography>
                        <List disablePadding dense>
                          {(aiPendingActions || []).map((action, idx) => {
                            const email = action.email || {};
                            const sender = email.from_name || email.from_address || 'Sconosciuto';
                            const subject = email.subject || '(nessun oggetto)';
                            const dateSent = email.date_sent ? format(new Date(email.date_sent), 'dd/MM HH:mm', { locale: it }) : '';
                            const categoryColors = {
                              clienti: 'primary', customer: 'primary',
                              fornitori: 'secondary',
                              comunicazioni: 'info', internal: 'info', other: 'info',
                              pubblicita: 'default', advertising: 'default', spam: 'default',
                            };
                            return (
                              <ListItem
                                key={action.id}
                                disableGutters
                                sx={{
                                  px: 0.5,
                                  py: 0.5,
                                  cursor: 'pointer',
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: 'action.hover' },
                                  borderLeft: 3,
                                  borderColor: action.ai_category === 'clienti' || action.ai_category === 'customer'
                                    ? 'primary.main'
                                    : action.ai_category === 'fornitori'
                                    ? 'secondary.main'
                                    : 'grey.300',
                                  mb: 0.5,
                                }}
                                onClick={() => navigate(`/crm/email/${email.id}`)}
                              >
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <EmailIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                                      <Typography variant="body2" noWrap fontWeight="medium" sx={{ flex: 1 }}>
                                        {sender}
                                      </Typography>
                                      {action.ai_category && (
                                        <Chip
                                          label={action.ai_category}
                                          size="small"
                                          color={categoryColors[action.ai_category] || 'default'}
                                          sx={{ height: 18, fontSize: '0.65rem' }}
                                        />
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="caption" noWrap sx={{ flex: 1 }} color="text.secondary">
                                        {subject}
                                      </Typography>
                                      {dateSent && (
                                        <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                                          {dateSent}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  secondaryTypographyProps={{ component: 'div' }}
                                  primaryTypographyProps={{ component: 'div' }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                        {aiPending > (aiPendingActions || []).length && (
                          <Button
                            size="small"
                            fullWidth
                            sx={{ mt: 0.5, fontSize: '0.75rem' }}
                            onClick={() => navigate('/crm/email/ai-dashboard')}
                          >
                            Vedi tutte ({aiPending})
                          </Button>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* ── Pannello Stato Impianti ── */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PropaneIcon color="info" />
                    <Typography variant="h6" fontWeight="bold">
                      Stato Impianti
                    </Typography>
                  </Box>
                  <Tooltip title="Vedi tutti gli impianti">
                    <IconButton size="small" onClick={() => navigate('/crm/impianti')}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {(plantsSummaryLoading && lowestPlantsLoading) ? (
                  <>
                    <Skeleton height={32} />
                    <Skeleton height={32} sx={{ mt: 1 }} />
                    <Skeleton height={32} sx={{ mt: 1 }} />
                  </>
                ) : plantsTotal === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Nessun impianto attivo
                  </Typography>
                ) : (
                  <>
                    {/* Barra stacked orizzontale */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', mb: 1 }}>
                        {plantsSummary.ok > 0 && (
                          <Box sx={{ flex: plantsSummary.ok, bgcolor: '#4caf50' }} />
                        )}
                        {plantsSummary.warning > 0 && (
                          <Box sx={{ flex: plantsSummary.warning, bgcolor: '#ff9800' }} />
                        )}
                        {plantsSummary.critical > 0 && (
                          <Box sx={{ flex: plantsSummary.critical, bgcolor: '#f44336' }} />
                        )}
                        {plantsSummary.noData > 0 && (
                          <Box sx={{ flex: plantsSummary.noData, bgcolor: '#bdbdbd' }} />
                        )}
                      </Box>
                      {/* Legenda */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                        {plantsSummary.ok > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#4caf50' }} />
                            <Typography variant="caption">OK ({plantsSummary.ok})</Typography>
                          </Box>
                        )}
                        {plantsSummary.warning > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff9800' }} />
                            <Typography variant="caption">Attenzione ({plantsSummary.warning})</Typography>
                          </Box>
                        )}
                        {plantsSummary.critical > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f44336' }} />
                            <Typography variant="caption">Critici ({plantsSummary.critical})</Typography>
                          </Box>
                        )}
                        {plantsSummary.noData > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#bdbdbd' }} />
                            <Typography variant="caption">N/D ({plantsSummary.noData})</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 1.5 }} />

                    {/* Top 5 impianti — livello stimato più basso */}
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                      Livello stimato più basso
                    </Typography>
                    {lowestPlantsLoading ? (
                      [...Array(3)].map((_, i) => <Skeleton key={i} height={36} sx={{ mb: 0.5 }} />)
                    ) : (lowestPlants || []).length === 0 ? (
                      <Alert severity="success" sx={{ py: 0 }}>
                        Tutti gli impianti sono a buon livello
                      </Alert>
                    ) : (
                      <List disablePadding dense>
                        {(lowestPlants || []).map((plant) => {
                          const level = plant.estimated_level_percent ?? plant.current_level_percent ?? 0;
                          const threshold = plant.low_level_threshold || 15;
                          return (
                            <ListItem
                              key={plant.id}
                              disableGutters
                              sx={{
                                px: 0,
                                cursor: 'pointer',
                                borderRadius: 1,
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                              onClick={() => navigate(`/crm/impianti/${plant.id}`)}
                            >
                              <ListItemText
                                primary={
                                  <Typography variant="body2" fontWeight="medium" noWrap>
                                    {plant.nome || plant.plant_code}
                                  </Typography>
                                }
                                secondary={plant.citta || ''}
                                secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                                sx={{ mr: 1 }}
                              />
                              <Box sx={{ width: 120, flexShrink: 0 }}>
                                <LevelBar percent={level} threshold={threshold} />
                              </Box>
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* ═══ ZONA 3 — Fatture Scadute (compatta) ═══ */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Fatture Scadute
            </Typography>
            <Button 
              size="small"
              variant="outlined" 
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/crm/fatture?filter=overdue')}
            >
              Vedi Tutte
            </Button>
          </Box>

          {overdueLoading ? (
            [...Array(3)].map((_, i) => (
              <Skeleton key={i} height={50} sx={{ mb: 0.5 }} />
            ))
          ) : (overdueInvoices?.length || 0) === 0 ? (
            <Alert severity="success">
              Nessuna fattura scaduta!
            </Alert>
          ) : (
            <List disablePadding dense>
              {(overdueInvoices || []).slice(0, 5).map((invoice, idx) => {
                const daysOverdue = invoice.giorni_scaduta || Math.floor(
                  (new Date() - new Date(invoice.f_ult_gior || invoice.f_emission)) / (1000 * 60 * 60 * 24)
                );
                
                return (
                  <ListItem 
                    key={invoice.id}
                    divider={idx < Math.min((overdueInvoices?.length || 0), 5) - 1}
                    secondaryAction={
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight="bold" color="error">
                          {formatCurrency(invoice.saldo_residuo)}
                        </Typography>
                        <Chip 
                          label={`${daysOverdue}gg`}
                          size="small"
                          color="error"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => navigate(`/crm/fatture/${invoice.id}`)}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="medium">
                          #{formatNuf(invoice.sezionale, invoice.nuf)} — {formatCustomerName(invoice.customer, invoice.f_nome || 'N/D')}
                        </Typography>
                      }
                      secondary={
                        invoice.f_ult_gior
                          ? `Scadenza: ${format(new Date(invoice.f_ult_gior), 'dd/MM/yyyy', { locale: it })}`
                          : undefined
                      }
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Snackbar feedback redeploy */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
